import React from 'react';
import styled from 'styled-components';
import { ReportAnswers } from '../../FeedbackReport';
import { BENEFICIARY_QUESTION_ID } from 'npo/pages/ReportBuilder/Components/Narrative/utils';
import {
  Beneficiaries,
  BeneficiaryAge,
} from 'npo/pages/ReportBuilder/Components/Beneficiary/utils/beneficiaryInterfaces';
import ReportBeneficiariesHeader from './ReportBeneficiariesHeader';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import ReportBeneficiariesRow from './ReportBeneficiariesRow';
import { getBeneficiaryNameLabel } from 'npo/pages/ReportBuilder/Components/ReportFinalize/FinalizeBeneficiaries/BeneficiaryCard';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import ReportSectionNote from '../ReportSectionNote';
import { NOTE_QUESTION_IDS } from 'npo/pages/ReportBuilder/constants';

const getBeneficiaries = (beneficiaries: Beneficiaries): JSX.Element[] => {
  const result: JSX.Element[] = [];
  for (const b in beneficiaries) {
    const key = b as keyof Beneficiaries;
    const categories = beneficiaries[key]?.categories;
    if (!categories) continue;
    for (const i of categories) {
      if (i.percentage === null) continue;
      result.push(
        <ReportBeneficiariesRow
          category={
            key === 'age'
              ? 'Age Groups**'
              : `${capitalizeFirstLetter(key)} Groups`
          }
          groupName={i.name}
          subGroupName={
            key === 'age'
              ? getBeneficiaryNameLabel(i.name as BeneficiaryAge)
              : undefined
          }
          percentage={i.percentage || 0}
        />,
      );
    }
  }
  return result;
};

const parseBeneficiaries = (
  answer: string | undefined,
): Beneficiaries | undefined => {
  if (!answer) return undefined;
  try {
    return JSON.parse(answer);
  } catch (e) {
    return undefined;
  }
};

interface ReportBeneficiariesProps {
  answers: ReportAnswers;
  isLegacy?: boolean;
  legacyDemographics?: Array<{
    category: string;
    group: string;
    subGroup?: string | null;
    served: number;
  }>;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  border-top: 1px solid ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayLight};
  padding-top: ${SPACERS.large};
  padding-bottom: ${SPACERS.large};
  margin-top: ${SPACERS.large};
  margin-bottom: ${SPACERS.large};
  .report-beneficiaries-header {
    padding-left: ${SPACERS.extraLarge};
    padding-right: ${SPACERS.extraLarge};
    padding-bottom: ${SPACERS.tiny};
  }
  .beneficiary-note {
    margin-top: ${SPACERS.small};
    padding-left: ${SPACERS.extraLarge};
    padding-right: ${SPACERS.extraLarge};
    .beneficiary-note-text {
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .beneficiaries-note {
    padding-bottom: 0;
    margin-top: ${SPACERS.medium};
    border-top: 1px solid ${COLORS.grayLight};
  }
`;

export const RenderBeneficiaries: React.FC<{
  beneficiaries: JSX.Element[];
  answers: ReportAnswers;
}> = ({ beneficiaries, answers }) => {
  return (
    <StyledDiv>
      <TrueImpactText
        variant="heading-subheading"
        className="report-beneficiaries-header"
      >
        Beneficiaries
      </TrueImpactText>
      <ReportBeneficiariesHeader />
      {beneficiaries}
      <div className="beneficiary-note">
        <TrueImpactText variant="body-small" className="beneficiary-note-text">
          *Not all groups contain sub-groups
        </TrueImpactText>
        <TrueImpactText variant="body-small">
          **Group total must equal 100 percent
        </TrueImpactText>
      </div>
      <ReportSectionNote
        answers={answers}
        label="Beneficiaries notes:"
        className="beneficiaries-note"
        questionId={NOTE_QUESTION_IDS.beneficiaries}
      />
    </StyledDiv>
  );
};

const ReportBeneficiaries: React.FC<ReportBeneficiariesProps> = ({
  answers,
  isLegacy,
  legacyDemographics,
}) => {
  // Legacy IR path: use provided demographics array
  if (isLegacy && legacyDemographics && legacyDemographics.length > 0) {
    const legacyRows = legacyDemographics.map((d, idx) => (
      <ReportBeneficiariesRow
        key={`${d.category}-${d.group}-${idx}`}
        category={d.category}
        groupName={d.group}
        subGroupName={d.subGroup || undefined}
        percentage={d.served}
      />
    ));
    return <RenderBeneficiaries answers={answers} beneficiaries={legacyRows} />;
  }

  // V4 path: parse beneficiaries from answers JSON
  const answer = answers.beneficiaries?.find(
    x => x.question.dbId === BENEFICIARY_QUESTION_ID,
  )?.text;
  const beneficiaries = parseBeneficiaries(answer);
  if (!beneficiaries) return null;

  const renderBeneficiaries = getBeneficiaries(beneficiaries);
  return (
    <RenderBeneficiaries
      answers={answers}
      beneficiaries={renderBeneficiaries}
    />
  );
};

export default ReportBeneficiaries;
